import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Globe,
  Bell,
  Moon,
  Sun,
  Monitor,
  FileText,
  Download,
  Mail,
  MessageSquare,
  Shield,
  Save,
  CheckCircle,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { Header } from "../components/Header";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { preferencesService } from "../lib/supabase";

interface PreferencesState {
  theme: "light" | "dark" | "system";
  language: string;
  emailNotifications: boolean;
  reportReady: boolean;
  weeklyDigest: boolean;
  marketingEmails: boolean;
  defaultReportFormat: "pdf" | "markdown" | "json";
  includeRawData: boolean;
  autoDownload: boolean;
  shareAnalytics: boolean;
  saveHistory: boolean;
}

export function PreferencesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme: currentTheme, setTheme: setGlobalTheme } = useTheme();
  
  const [preferences, setPreferences] = useState<PreferencesState>({
    theme: currentTheme,
    language: "en",
    emailNotifications: true,
    reportReady: true,
    weeklyDigest: false,
    marketingEmails: false,
    defaultReportFormat: "pdf",
    includeRawData: false,
    autoDownload: true,
    shareAnalytics: true,
    saveHistory: true,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState("");

  // Sync theme from context
  useEffect(() => {
    setPreferences((prev) => ({ ...prev, theme: currentTheme }));
  }, [currentTheme]);

  // Load preferences on mount
  useEffect(() => {
    async function loadPreferences() {
      if (!user?.id) return;
      
      setIsLoading(true);
      try {
        const prefs = await preferencesService.getPreferences(user.id);
        if (prefs) {
          setPreferences({
            theme: currentTheme, // Use global theme
            language: prefs.language || "en",
            emailNotifications: prefs.email_notifications ?? true,
            reportReady: prefs.report_ready_notifications ?? true,
            weeklyDigest: prefs.weekly_digest ?? false,
            marketingEmails: prefs.marketing_emails ?? false,
            defaultReportFormat: prefs.default_report_format || "pdf",
            includeRawData: prefs.include_raw_data ?? false,
            autoDownload: prefs.auto_download ?? true,
            shareAnalytics: prefs.share_analytics ?? true,
            saveHistory: prefs.save_history ?? true,
          });
        }
      } catch (err) {
        console.error("Failed to load preferences:", err);
      } finally {
        setIsLoading(false);
      }
    }

    loadPreferences();
  }, [user, currentTheme]);

  const handleToggle = (key: keyof PreferencesState) => {
    setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleChange = (key: keyof PreferencesState, value: string) => {
    setPreferences((prev) => ({ ...prev, [key]: value }));
    
    // Immediately apply theme change
    if (key === "theme") {
      setGlobalTheme(value as "light" | "dark" | "system");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;

    setIsSaving(true);
    setError("");
    setSaveSuccess(false);

    try {
      await preferencesService.savePreferences(user.id, {
        theme: preferences.theme,
        language: preferences.language,
        email_notifications: preferences.emailNotifications,
        report_ready_notifications: preferences.reportReady,
        weekly_digest: preferences.weeklyDigest,
        marketing_emails: preferences.marketingEmails,
        default_report_format: preferences.defaultReportFormat,
        include_raw_data: preferences.includeRawData,
        auto_download: preferences.autoDownload,
        share_analytics: preferences.shareAnalytics,
        save_history: preferences.saveHistory,
      });
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error("Failed to save preferences:", err);
      setError("Failed to save preferences. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const Toggle = ({ enabled, onToggle }: { enabled: boolean; onToggle: () => void }) => (
    <button
      type="button"
      onClick={onToggle}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        enabled ? "bg-blue-600" : "bg-slate-200"
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          enabled ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 transition-colors duration-200">
        <Header />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 transition-colors duration-200">
      <Header />

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Preferences</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-2">
            Customize your Insura AI experience
          </p>
        </div>

        {/* Success Message */}
        {saveSuccess && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <p className="text-green-700">Preferences saved successfully!</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Appearance */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 transition-colors">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <Monitor className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Appearance</h2>
            </div>

            <div className="space-y-6">
              {/* Theme */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                  Theme
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: "light", icon: Sun, label: "Light" },
                    { value: "dark", icon: Moon, label: "Dark" },
                    { value: "system", icon: Monitor, label: "System" },
                  ].map(({ value, icon: Icon, label }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => handleChange("theme", value)}
                      className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 transition-all ${
                        preferences.theme === value
                          ? "border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                          : "border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500 text-slate-600 dark:text-slate-300"
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Language */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Language
                </label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <select
                    value={preferences.language}
                    onChange={(e) => handleChange("language", e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white dark:bg-slate-700 dark:text-white"
                  >
                    <option value="en">English</option>
                    <option value="tr">Türkçe</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Notifications */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 transition-colors">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                <Bell className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Notifications</h2>
            </div>

            <div className="space-y-4">
              {[
                {
                  key: "emailNotifications" as const,
                  icon: Mail,
                  title: "Email Notifications",
                  description: "Receive email notifications for important updates",
                },
                {
                  key: "reportReady" as const,
                  icon: FileText,
                  title: "Report Ready",
                  description: "Get notified when your analysis report is ready",
                },
                {
                  key: "weeklyDigest" as const,
                  icon: MessageSquare,
                  title: "Weekly Digest",
                  description: "Receive a weekly summary of your activity",
                },
                {
                  key: "marketingEmails" as const,
                  icon: Mail,
                  title: "Marketing Emails",
                  description: "Receive news, updates, and special offers",
                },
              ].map(({ key, icon: Icon, title, description }) => (
                <div
                  key={key}
                  className="flex items-center justify-between py-3 border-b border-slate-100 dark:border-slate-700 last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <Icon className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">{title}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">{description}</p>
                    </div>
                  </div>
                  <Toggle
                    enabled={preferences[key]}
                    onToggle={() => handleToggle(key)}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Report Settings */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 transition-colors">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Report Settings</h2>
            </div>

            <div className="space-y-6">
              {/* Default Format */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Default Report Format
                </label>
                <div className="relative">
                  <Download className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <select
                    value={preferences.defaultReportFormat}
                    onChange={(e) => handleChange("defaultReportFormat", e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white dark:bg-slate-700 dark:text-white"
                  >
                    <option value="pdf">PDF Document</option>
                    <option value="markdown">Markdown</option>
                    <option value="json">JSON Data</option>
                  </select>
                </div>
              </div>

              {/* Toggle Options */}
              <div className="space-y-4">
                {[
                  {
                    key: "includeRawData" as const,
                    title: "Include Raw Data",
                    description: "Include raw AI analysis data in reports",
                  },
                  {
                    key: "autoDownload" as const,
                    title: "Auto Download",
                    description: "Automatically download reports when ready",
                  },
                ].map(({ key, title, description }) => (
                  <div
                    key={key}
                    className="flex items-center justify-between py-3 border-b border-slate-100 dark:border-slate-700 last:border-0"
                  >
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">{title}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">{description}</p>
                    </div>
                    <Toggle
                      enabled={preferences[key]}
                      onToggle={() => handleToggle(key)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Privacy */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 transition-colors">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <Shield className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Privacy</h2>
            </div>

            <div className="space-y-4">
              {[
                {
                  key: "shareAnalytics" as const,
                  title: "Share Analytics",
                  description: "Help us improve by sharing anonymous usage data",
                },
                {
                  key: "saveHistory" as const,
                  title: "Save Case History",
                  description: "Keep a history of your analysis cases",
                },
              ].map(({ key, title, description }) => (
                <div
                  key={key}
                  className="flex items-center justify-between py-3 border-b border-slate-100 dark:border-slate-700 last:border-0"
                >
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">{title}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{description}</p>
                  </div>
                  <Toggle
                    enabled={preferences[key]}
                    onToggle={() => handleToggle(key)}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSaving}
              className="flex items-center gap-2 px-6 py-3 bg-slate-900 dark:bg-blue-600 text-white rounded-xl hover:bg-slate-800 dark:hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Save className="w-5 h-5" />
              )}
              {isSaving ? "Saving..." : "Save Preferences"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
